/**
 * AlacarteCrossSell — recommends à la carte features that complement
 * the member's tier. v3.1 also surfaces the new Community Add-On
 * ($9.99/mo) and 3-Community Bundle ($24.99/mo) for Pulse / Surge /
 * Spectrum members. Hidden for Attuned (already includes all 9
 * communities) and Signal (must upgrade first).
 */
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Users } from "lucide-react";
import { useProfile } from "@/lib/profile/useProfile";
import type { TierId } from "@/lib/profile/types";

interface Pick {
  key: string;
  name: string;
  pitch: string;
}

const PICKS: Record<TierId, Pick[]> = {
  tier_signal: [
    { key: "message_reading", name: "Message Reading Pack", pitch: "Read 25 incoming messages without upgrading." },
    { key: "amplify_signal", name: "Amplify a Signal", pitch: "Get one Signal seen by 5x more people." },
  ],
  tier_pulse: [
    { key: "roam_mode", name: "Roam Mode", pitch: "Match in another city for 24 hours." },
    { key: "profile_spotlight", name: "Profile Spotlight", pitch: "Featured for 60 minutes during peak hours." },
  ],
  tier_surge: [
    { key: "priority_matching", name: "Priority Matching", pitch: "Skip to the front of the queue for a week." },
    { key: "voice_wave_plus", name: "Voice Wave+", pitch: "60-second voice intros instead of 30." },
  ],
  tier_spectrum: [
    { key: "event_pass", name: "Event Pass", pitch: "VIP entry to one community event of your choice." },
    { key: "replay_pack", name: "Replay Pack", pitch: "See profiles you've already passed on." },
  ],
  tier_attuned: [
    { key: "community_verified", name: "Community Verified", pitch: "Wear the verified badge across all 9 communities." },
    { key: "event_host_pass", name: "Event Host Pass", pitch: "Host your own community event with full tools." },
  ],
};

/** Tiers eligible to buy community add-ons (per v3.1). */
const ADDON_ELIGIBLE: TierId[] = ["tier_pulse", "tier_surge", "tier_spectrum"];

const AlacarteCrossSell = () => {
  const { profile } = useProfile();
  if (!profile) return null;
  const items = PICKS[profile.tier] ?? [];
  const showAddons = ADDON_ELIGIBLE.includes(profile.tier);

  return (
    <div className="space-y-4">
      {showAddons && (
        <section className="rounded-2xl border border-primary/30 bg-primary/5 p-6">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Users className="h-4 w-4 text-primary" /> Add another community
          </h2>
          <p className="mt-1 text-xs text-muted-foreground">
            Want a second or third community without going all-in on Attuned? Pick one or grab the bundle.
          </p>
          <ul className="mt-4 grid gap-3 sm:grid-cols-2">
            <li>
              <Link
                to="/a-la-carte#community_addon_single"
                className="block h-full rounded-xl border border-border/60 px-4 py-3 hover:bg-secondary/40 transition-colors"
              >
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm font-medium">Community Add-On</p>
                  <span className="text-xs text-muted-foreground">$9.99/mo</span>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  Add one extra community to your plan.
                </p>
              </Link>
            </li>
            <li>
              <Link
                to="/a-la-carte#community_addon_bundle"
                className="block h-full rounded-xl border border-border/60 px-4 py-3 hover:bg-secondary/40 transition-colors"
              >
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm font-medium">3-Community Bundle</p>
                  <span className="text-xs text-muted-foreground">$24.99/mo</span>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  Add three communities — saves vs. buying individually.
                </p>
              </Link>
            </li>
          </ul>
          <p className="mt-3 text-[11px] text-muted-foreground">
            Want all 9?{" "}
            <Link to="/pricing" className="underline hover:text-foreground">
              Upgrade to Attuned ($99.99/mo)
            </Link>
            .
          </p>
        </section>
      )}

      {items.length > 0 && (
        <section className="rounded-2xl border border-border bg-card p-6">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" /> Pair with à la carte
          </h2>
          <p className="mt-1 text-xs text-muted-foreground">
            Hand-picked add-ons for your tier. One-time or monthly. Cancel anytime.
          </p>
          <ul className="mt-4 grid gap-3 sm:grid-cols-2">
            {items.map((p) => (
              <li key={p.key}>
                <Link
                  to={`/a-la-carte#${p.key}`}
                  className="block h-full rounded-xl border border-border/60 px-4 py-3 hover:bg-secondary/40 transition-colors"
                >
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-medium">{p.name}</p>
                    <ArrowRight className="h-3.5 w-3.5 text-muted-foreground mt-0.5 shrink-0" />
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">{p.pitch}</p>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
};

export default AlacarteCrossSell;
